import { useCallback, useState } from 'react'
import { View, Text } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useScreenInsets } from '@/lib/hooks/use-screen-insets'
import Animated from 'react-native-reanimated'
import { Image as ExpoImage } from 'expo-image'
import { toPlaceholder } from '@/lib/utils/blurhash'
import {
  Fuel,
  Users,
  DoorOpen,
  Gauge,
  Zap,
  MapPin,
  Shield,
  ChevronRight,
  Edit3,
  Lock,
  MoreVertical,
  BadgeCheck,
  UserCheck,
} from '@/components/ui/icons'
import { isInspectionPhotoRequirement } from '@/lib/types/listing-photo.types'
import { BlockUserButton } from '@/components/ui/BlockUserButton'
import { useAuth } from '@/lib/hooks/use-auth'
import { messagingService } from '@/lib/services/messaging.service'
import { toast } from '@/lib/toast'
import { notification, NotificationType } from '@/lib/utils/haptics'
import { useCanMessageHost } from '@/lib/hooks/use-can-message-host'
import { useBookingEligibility } from '@/lib/hooks/use-booking-eligibility'
import { orderedPendingSteps } from '@/lib/verification/steps'
import { vehicleHasPlatformCoverage } from '@/lib/utils/qualitas-asr'
import { ReadyToBook } from '@/components/booking/ReadyToBook'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { BackButton } from '@/components/ui/BackButton'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { Avatar } from '@/components/ui/Avatar'
import { Rating } from '@/components/ui/Rating'
import { Skeleton } from '@/components/ui/Skeleton'
import { Pressable } from '@/components/ui/Pressable'
import { PhotoLightbox, usePhotoLightbox } from '@/components/ui/PhotoLightbox'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { useVehicleDetail } from '@/lib/hooks/use-vehicle-detail'
import { usePressAction } from '@/lib/hooks/use-press-action'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import {
  useSemanticColors,
  SPACING,
  RADIUS,
  INTERACTION,
  Z_INDEX,
  withAlpha,
} from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { useTheme } from '@/lib/hooks/use-theme'
import { useEntrance } from '@/lib/hooks/use-entrance'
import { getVehicleDisplayPriceCents } from '@/lib/utils/pricing'
import { vehicleHasQualitasAsrVerification } from '@/lib/utils/qualitas-asr'
import { formatPrice } from '@/lib/utils/format'
import { formatBookingMonthYear } from '@/lib/utils/dates'
import { marketTransmissionI18nKey } from '@/lib/utils/transmission'
import { isPriorityVehicleFeature, sortVehicleFeatures } from '@/lib/utils/vehicle-features'

/**
 * Longer than this and a spec value cannot share a line with its label
 * without both columns wrapping into each other. "Modelo de seguro" /
 * "Póliza propia del anfitrión, verificada" turned one tidy row into a
 * ragged four-line block with the two columns colliding mid-card, so past
 * this length the value drops to its own full-width line under the label.
 */
const SPEC_VALUE_STACK_THRESHOLD = 24

function SpecItem({
  icon: Icon,
  label,
  value,
  colors,
}: {
  icon: typeof Fuel
  label: string
  value: string
  colors: ReturnType<typeof useSemanticColors>
}) {
  const stacked = value.length > SPEC_VALUE_STACK_THRESHOLD

  const labelStyle = {
    fontSize: 14,
    lineHeight: 20,
    fontFamily: FONTS.body,
    color: colors.textSecondary,
  } as const

  const valueStyle = {
    fontSize: 14,
    lineHeight: 20,
    fontFamily: FONTS.bodyBold,
    color: colors.textPrimary,
  } as const

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: SPACING.sm,
        paddingVertical: SPACING.xs,
      }}
    >
      <View style={{ width: 22, alignItems: 'center', paddingTop: 2, flexShrink: 0 }}>
        <Icon size={18} color={colors.textSecondary} strokeWidth={1.8} />
      </View>
      {stacked ? (
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={labelStyle}>{label}</Text>
          <Text style={valueStyle}>{value}</Text>
        </View>
      ) : (
        <>
          <Text style={{ ...labelStyle, flex: 1, minWidth: 0 }}>{label}</Text>
          <Text style={{ ...valueStyle, flex: 1.2, minWidth: 0, textAlign: 'right' }}>{value}</Text>
        </>
      )}
    </View>
  )
}

/**
 * Hero heights, shared by the loading skeleton and the real gallery.
 *
 * They used to be typed twice — 240 in the skeleton, 280/400 in the content —
 * so the swap from skeleton to photo shifted every block below it. A skeleton
 * that is not the same shape as what replaces it is a layout jump with extra
 * steps.
 */
const HERO_HEIGHT_MOBILE = 280
const HERO_HEIGHT_DESKTOP = 400

export default function VehicleDetailScreen() {
  const { isDesktop } = useBreakpoint()

  // The screen container renders from ONE JSX position. The four branches
  // below (loading, unavailable, desktop, mobile) each used to re-wrap their
  // own copy, so the container was torn down and rebuilt on every transition.
  // `maxWidth` is the one prop that legitimately differed between them, so it
  // is computed here instead of duplicating the element.
  return (
    <ScreenContainer scroll={false} card={false} maxWidth={isDesktop ? 1100 : undefined}>
      <VehicleDetailBody />
    </ScreenContainer>
  )
}

function VehicleDetailBody() {
  const { vehicleId } = useLocalSearchParams<{ vehicleId: string }>()
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useScreenInsets()
  const { user } = useAuth()
  const { vehicle, isLoading, error, refetch } = useVehicleDetail(vehicleId)
  const enter = useEntrance(!isLoading)
  const { isDesktop } = useBreakpoint()
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null)
  const lightbox = usePhotoLightbox()
  const transmissionLabelKey = marketTransmissionI18nKey(
    'listing.transmissions',
    vehicle?.transmission,
  )

  const handleBookNow = usePressAction(() => {
    if (!vehicle?.id || vehicle.status !== 'active') return
    router.push({
      pathname: '/booking/[vehicleId]',
      params: { vehicleId: vehicle.id },
    })
  })

  /*
   * La elegibilidad se pregunta ACA, no en el checkout.
   *
   * Vivía en un solo lugar de toda la app --checkout.tsx, la ultima pantalla--
   * asi que la persona descubria los cinco requisitos despues de elegir carro,
   * fechas y puntos de entrega. Medido: de 940 que abrieron una ficha, 885
   * nunca abrieron la pantalla de verificacion. No fallaron: nunca empezaron.
   */
  const bookingGate = useBookingEligibility(vehicle ? vehicleHasPlatformCoverage(vehicle) : null)
  const needsVerification = bookingGate.resolved && !bookingGate.allowed

  const messagingGate = useCanMessageHost(vehicle?.host?.id)
  const messagingLocked = messagingGate.resolved && !messagingGate.allowed

  const handleContactHost = usePressAction(
    useCallback(async () => {
      if (!user || !vehicle?.host?.id) return

      /*
       * Preguntar antes de invitar.
       *
       * Esto mostraba "No tenés permisos para esta acción" —dos veces, porque
       * nada desactivaba el botón— cuando en realidad lo que falta es una
       * reserva pagada. La regla no cambia: no se habla con nadie hasta pagar.
       * Lo que cambia es que se dice qué falta y se ofrece el paso siguiente,
       * en vez de dejar a la persona en un callejón.
       *
       * `resolved` y no `!allowed`: mientras la consulta no haya respondido se
       * sigue de largo y decide el servidor. Bloquear a alguien que SÍ puede
       * escribir, porque se cayó una consulta, es peor.
       */
      /*
       * UN SOLO DESENLACE, SE LLEGUE POR DONDE SE LLEGUE.
       *
       * El candado se puede descubrir de dos maneras: porque el gate ya
       * respondió, o porque no había respondido todavía y contestó el servidor.
       * Es el MISMO hecho, y antes tenía dos tratos distintos: el gate explicaba
       * y ofrecía reservar; el catch mostraba un toast de error, escribía en la
       * consola y mandaba el fallo a Sentry.
       *
       * Lo encontró la suite en tablet, donde la consulta del gate todavía no
       * había respondido cuando se tocó la tarjeta. Un desenlace de producto
       * reportado como excepción ensucia Sentry y hace que un comportamiento
       * diseñado parezca una avería.
       */
      const explainTheLock = () => {
        void notification(NotificationType.Warning)
        toast.info(t('messages.locked_until_paid'))
        if (vehicle.status === 'active') {
          router.push({
            pathname: '/booking/[vehicleId]',
            params: { vehicleId: vehicle.id },
          })
        }
      }

      if (messagingGate.resolved && !messagingGate.allowed) {
        explainTheLock()
        return
      }

      try {
        const conversationId = await messagingService.getOrCreateConversation(
          user.id,
          vehicle.host.id,
        )
        router.push(`/(tabs)/messages/${conversationId}`)
      } catch (err) {
        // El 42501 de este camino significa exactamente una cosa: la reserva no
        // está pagada. No es un error que reportar sino la regla del producto,
        // así que se explica igual que arriba y no se registra en ningún lado.
        if ((err as { code?: string })?.code === '42501') {
          explainTheLock()
          return
        }

        if (__DEV__) console.error('[VehicleDetail] contact host failed:', err)
        toast.error(errorMessage(err, t('common.error')))
        captureError(err, { screen: 'explore/vehicle', action: 'contact-host' })
      }
    }, [messagingGate.resolved, messagingGate.allowed, router, t, user, vehicle]),
  )

  if (isLoading) {
    return (
      <>
        <BackButton
          fallbackHref="/(tabs)/explore"
          preferFallback
          style={{ marginLeft: SPACING.lg, marginTop: SPACING.sm }}
        />
        {/* Mobile hero is full-bleed and square-cornered; desktop is an inset
            rounded grid. The skeleton mirrors both so nothing below it moves
            when the photo arrives. */}
        <Skeleton
          width="100%"
          height={isDesktop ? HERO_HEIGHT_DESKTOP : HERO_HEIGHT_MOBILE}
          borderRadius={isDesktop ? RADIUS.lg : 0}
        />
        <View style={{ padding: SPACING.lg, gap: SPACING.md }}>
          <Skeleton width="70%" height={24} borderRadius={RADIUS.sm} />
          <Skeleton width="40%" height={18} borderRadius={RADIUS.sm} />
          <Skeleton width="100%" height={120} borderRadius={RADIUS.lg} />
        </View>
      </>
    )
  }

  // A transport failure and a car that is genuinely gone are different facts
  // and used to share one screen. Someone on a bad connection was told the
  // listing had been "pausado, eliminado o reservado" — a claim about the
  // host's vehicle — and the only way out was back to the list, losing the
  // link they had opened.
  if (error) {
    return (
      <>
        <BackButton
          fallbackHref="/(tabs)/explore"
          preferFallback
          style={{ marginLeft: SPACING.lg, marginTop: SPACING.sm }}
        />
        <View style={{ flex: 1, justifyContent: 'center', padding: SPACING.xl }}>
          <EmptyState
            unavailable
            variant="aurora"
            title={t('common.error_title')}
            description={t('common.error_description')}
            action={{ label: t('common.retry'), onPress: () => void refetch() }}
            secondaryAction={{
              label: t('vehicle_detail.browse_available'),
              onPress: () => router.replace('/(tabs)/explore'),
            }}
          />
        </View>
      </>
    )
  }

  if (!vehicle) {
    return (
      <>
        <BackButton
          fallbackHref="/(tabs)/explore"
          preferFallback
          style={{ marginLeft: SPACING.lg, marginTop: SPACING.sm }}
        />
        <View
          style={{
            flex: 1,
            justifyContent: 'center',
            padding: SPACING.xl,
            gap: SPACING.md,
          }}
        >
          {/* Marcador de estado: esta ruta no pudo mostrar el vehiculo.
              Lo lee screen-matrix. Ver EmptyState.unavailable. */}
          <View testID="screen-state.unavailable" style={{ width: 0, height: 0 }} />
          <Text
            style={{
              fontSize: 24,
              lineHeight: 30,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
              textAlign: 'center',
            }}
          >
            {t('vehicle_detail.unavailable_title')}
          </Text>
          <Text
            style={{
              fontSize: 15,
              lineHeight: 22,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
              textAlign: 'center',
            }}
          >
            {t('vehicle_detail.unavailable_body')}
          </Text>
          <Button
            onPress={() => router.replace('/(tabs)/explore')}
            variant="primary"
            size="lg"
            title={t('vehicle_detail.browse_available')}
            testID="vehicle-detail.browse-available"
          />
        </View>
      </>
    )
  }

  const isOwner = !!user && user.id === vehicle.host?.id
  const isBookable = vehicle.status === 'active'
  const sticker = getVehicleDisplayPriceCents(vehicle)
  const ratePrice = formatPrice(sticker.cents)
  const rateLabel =
    sticker.unit === 'hour' ? t('vehicle_detail.per_hour') : t('vehicle_detail.per_day')
  const allImages = vehicle.images ?? []
  const showcaseImages = allImages.filter((img) => !isInspectionPhotoRequirement(img.requirementId))
  const inspectionImages = allImages.filter((img) =>
    isInspectionPhotoRequirement(img.requirementId),
  )
  const galleryImages = showcaseImages.length > 0 ? showcaseImages : allImages
  const defaultImage = galleryImages.length
    ? (galleryImages.find((img) => img.isPrimary) ?? galleryImages[0])
    : null
  const heroImage = allImages.find((img) => img.id === selectedImageId) ?? defaultImage

  const toLightboxImages = (photos: typeof galleryImages) =>
    photos.map((img) => ({
      id: img.id,
      uri: img.url,
      blurhash: img.blurhash,
      accessibilityLabel: vehicle.title,
    }))

  const openGalleryLightbox = (id: string) => {
    setSelectedImageId(id)
    const index = galleryImages.findIndex((img) => img.id === id)
    lightbox.open(toLightboxImages(galleryImages), index < 0 ? 0 : index)
  }

  const openInspectionLightbox = (id: string) => {
    const index = inspectionImages.findIndex((img) => img.id === id)
    lightbox.open(toLightboxImages(inspectionImages), index < 0 ? 0 : index)
  }

  const lightboxEl = (
    <PhotoLightbox
      visible={lightbox.visible}
      images={lightbox.images}
      index={lightbox.index}
      onIndexChange={(next) => {
        lightbox.setIndex(next)
        const shown = lightbox.images[next]
        if (shown && galleryImages.some((img) => img.id === shown.id)) {
          setSelectedImageId(shown.id)
        }
      }}
      onClose={lightbox.close}
    />
  )

  // The badge row used to render unconditionally, so on a vehicle with no
  // rating and no badges it still spent its marginTop and left an 82pt hole
  // between the price and the description — three times the title→price step.
  const showComingSoonBadge = !isBookable && !isOwner
  const showInstantBookBadge = !!vehicle.isInstantBook && isBookable
  const hasBadges = showComingSoonBadge || showInstantBookBadge || isOwner
  // ── Shared content blocks ──────────────────────────────────────────────────

  const titleBlock = (
    <Animated.View collapsable={false} entering={enter(1)} style={{ marginTop: SPACING.md }}>
      <Text
        style={{
          fontSize: isDesktop ? 28 : 24,
          fontFamily: FONTS.bodyBold,
          color: colors.textPrimary,
          letterSpacing: -0.5,
        }}
      >
        {vehicle.title}
      </Text>
      {!isDesktop && (
        <View style={{ flexDirection: 'row', alignItems: 'baseline', marginTop: SPACING.xs }}>
          {/* Every other price surface — list cards, carousel cards, the
              docked bar — is textPrimary. This one was a fixed
              COLORS.amber.dark in both themes, i.e. the instant-book accent
              worn as a price, and a muddy brown on the dark background. */}
          <Text
            testID="vehicle-detail.price"
            style={{ fontSize: 22, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}
          >
            {ratePrice}
          </Text>
          <Text style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textSecondary }}>
            {rateLabel}
          </Text>
        </View>
      )}
      {/*
        EL DEPOSITO SE DICE ACA, NO DESPUES DE ELEGIR FECHAS.
        Medido el 2026-08-25: la ficha mostraba "$65 /dia" y la palabra
        "deposito" no aparecia en ninguna parte. El monto recien salia en el
        desglose, despues de elegir fechas y puntos de entrega. Para la L200 eso
        es una sorpresa de $500 sobre una tarifa de $65 -- 7,7 veces el precio
        anunciado. Cinco de los 30 carros visibles piden deposito, de $50 a $500.
        El dato ya venia cargado en `securityDepositCents`; solo no se pintaba.
        Va como linea secundaria y no sumado al titular: el precio por dia sigue
        siendo el precio por dia, y esto es lo que se retiene y se devuelve.
      */}
      {(vehicle.securityDepositCents ?? 0) > 0 && (
        <Text
          testID="vehicle-detail.deposit"
          style={{
            fontSize: 13,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
            marginTop: SPACING.xs,
          }}
        >
          {t('booking.security_deposit')} · {formatPrice(vehicle.securityDepositCents ?? 0)}
        </Text>
      )}
      {vehicle.ratingAvg != null && vehicle.ratingCount > 0 && (
        <View style={{ marginTop: SPACING.xs }}>
          <Rating value={vehicle.ratingAvg} count={vehicle.ratingCount} size={14} showLabel />
        </View>
      )}
      {vehicle.rentedDays > 0 ? (
        <Text
          style={{
            fontSize: 13,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
            marginTop: SPACING.xs,
          }}
        >
          {t('explore.rented_days', { count: vehicle.rentedDays })}
        </Text>
      ) : null}
      {hasBadges && (
        <View
          style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.sm, marginTop: SPACING.sm }}
        >
          {showComingSoonBadge && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
              <Shield size={14} color={COLORS.purple.DEFAULT} />
              <Text style={{ fontSize: 13, fontFamily: FONTS.bodyBold, color: colors.textBrand }}>
                {t('vehicle_detail.booking_coming_soon')}
              </Text>
            </View>
          )}
          {showInstantBookBadge && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
              <Zap size={14} color={COLORS.amber.dark} fill={COLORS.amber.dark} />
              <Text style={{ fontSize: 13, fontFamily: FONTS.bodyBold, color: COLORS.amber.dark }}>
                {t('vehicle_detail.instant_book_badge')}
              </Text>
            </View>
          )}
          {isOwner && (
            <View
              style={{
                paddingHorizontal: SPACING.sm,
                paddingVertical: SPACING.xs,
                backgroundColor: `${COLORS.purple.DEFAULT}15`,
                borderRadius: RADIUS.full,
              }}
            >
              <Text style={{ fontSize: 12, fontFamily: FONTS.bodyBold, color: colors.textBrand }}>
                {t('vehicle_detail.your_vehicle')}
              </Text>
            </View>
          )}
        </View>
      )}
    </Animated.View>
  )

  const descriptionBlock = vehicle.description ? (
    <Animated.View collapsable={false} entering={enter(2)} style={{ marginTop: SPACING.lg }}>
      <Text
        style={{
          fontSize: 14,
          fontFamily: FONTS.body,
          color: colors.textSecondary,
          lineHeight: 22,
        }}
      >
        {vehicle.description}
      </Text>
    </Animated.View>
  ) : null

  /*
   * Va INMEDIATAMENTE despues del titulo y el precio, arriba de todo lo demas.
   *
   * El sitio importa tanto como el contenido: si esto queda debajo de las
   * caracteristicas y las fotos de inspeccion, lo ve la misma gente que ya
   * bajaba --que no es la que se va--. La medicion dice que 885 de 940 nunca
   * abrieron la verificacion; a esa gente hay que alcanzarla antes del primer
   * scroll.
   */
  /*
   * UN SOLO BOTON DE RESERVAR, PARA LOS DOS ARMAZONES.
   *
   * Habia dos copias --una en la barra fija del movil y otra en la tarjeta de
   * precio de escritorio-- y divergieron: el movil aprendio a decir "Verificarme
   * para reservar" cuando falta un tramite, y el de escritorio se quedo diciendo
   * "Reservar". Asi que en app.tago.rent la columna izquierda decia "Te faltan 4
   * pasos" y la derecha invitaba a reservar igual, y la persona se enteraba
   * recien en el checkout de lo que ya habia leido.
   *
   * Es el mismo defecto que los seis tramites con tres vocabularios y los siete
   * filtros de ruido: dos copias de una decision no se mantienen iguales solas.
   * `size` es lo unico que legitimamente difiere entre los dos sitios.
   */
  const bookButton = (size: 'md' | 'lg') =>
    isOwner ? (
      <Button
        onPress={() => router.push(`/hosting/new-listing/details?vehicleId=${vehicle.id}`)}
        variant="secondary"
        size={size}
        title={t('common.edit')}
        icon={<Edit3 size={16} color={COLORS.purple.DEFAULT} strokeWidth={2} />}
        testID="vehicle-detail.edit-button"
      />
    ) : (
      <Button
        /*
         * Un boton que dice "Reservar" y lleva a verificacion es una mentira
         * barata de escribir y cara de cobrar. Cuando falta algo, dice lo que va
         * a pasar y lleva ahi.
         */
        onPress={
          isBookable && needsVerification
            ? () => {
                /*
                 * AL PASO EXACTO, NO AL HUB.
                 *
                 * "Verificarme para reservar" sobre lo único que falta —el
                 * cuestionario de Qualitas— tiene que abrir ESE cuestionario.
                 * Antes mandaba al hub de verificación, donde todo lo demás
                 * está en verde y no había ninguna tarjeta de Qualitas si la
                 * persona nunca empezó: dos usuarios quedaron ahí sin poder
                 * responder, con el servidor exigiéndolo igual.
                 *
                 * `orderedPendingSteps` es la misma lista que pinta
                 * `ReadyToBook`, así que la ficha y el botón no pueden
                 * divergir. El origen viaja para que la flecha de atrás —y el
                 * final del trámite— devuelvan a ESTE carro.
                 */
                const origin = `/explore/${vehicle.id}`
                const first = orderedPendingSteps(bookingGate.missing)[0]
                const target = first
                  ? `${first.href}?from=${encodeURIComponent(origin)}`
                  : `/profile/verification?from=${encodeURIComponent(origin)}`
                router.push(target as never)
              }
            : handleBookNow
        }
        disabled={!isBookable}
        variant="primary"
        size={size}
        title={
          !isBookable
            ? t('vehicle_detail.booking_coming_soon')
            : needsVerification
              ? t('verification.cta_verify_to_book')
              : t('vehicle_detail.book_now')
        }
        testID="vehicle-detail.book-button"
      />
    )

  const readyBlock = (
    <View style={{ marginTop: SPACING.md }}>
      <ReadyToBook missing={bookingGate.missing} resolved={bookingGate.resolved} />
    </View>
  )

  const specsBlock = (
    <Animated.View
      collapsable={false}
      entering={enter(3)}
      style={{
        marginTop: SPACING.lg,
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        padding: SPACING.md,
        ...WEB_SHADOWS.sm,
      }}
    >
      <Text
        style={{
          fontSize: 16,
          fontFamily: FONTS.bodyBold,
          color: colors.textPrimary,
          marginBottom: SPACING.sm,
        }}
      >
        {t('vehicle_detail.specs')}
      </Text>
      <SpecItem
        icon={Fuel}
        label={t('listing.fuel_type')}
        value={t(`listing.fuel_types.${vehicle.fuelType}`)}
        colors={colors}
      />
      <SpecItem
        icon={Gauge}
        label={t('listing.transmission')}
        value={transmissionLabelKey ? t(transmissionLabelKey) : ''}
        colors={colors}
      />
      <SpecItem
        icon={Users}
        label={t('listing.seats')}
        value={`${vehicle.seats}`}
        colors={colors}
      />
      <SpecItem
        icon={DoorOpen}
        label={t('listing.doors')}
        value={`${vehicle.doors}`}
        colors={colors}
      />
      {vehicle.dailyKmLimit ? (
        <SpecItem
          icon={MapPin}
          label={t('listing.km_limit')}
          value={`${vehicle.dailyKmLimit} km`}
          colors={colors}
        />
      ) : null}
      {/* Says who carries the policy, and nothing we cannot see.
          Naming Qualitas here is the same claim as the inspection card:
          only `vehicleHasQualitasAsrVerification`. Otherwise agency or
          host copy — never "verificada por ASR" on a car Qualitas does
          not cover. */}
      <SpecItem
        icon={Shield}
        label={t('listing.insurance_model')}
        value={
          vehicleHasQualitasAsrVerification(vehicle)
            ? t('vehicle_detail.qualitas_asr_coverage')
            : vehicle.organizationName
              ? t('vehicle_detail.agency_insurance_coverage', { orgName: vehicle.organizationName })
              : vehicle.coveragePath === 'exempt'
                ? t('vehicle_detail.no_platform_coverage')
                : t('vehicle_detail.host_policy_coverage')
        }
        colors={colors}
      />
    </Animated.View>
  )

  const sortedFeatures = sortVehicleFeatures(vehicle.features)
  const priorityFeatures = sortedFeatures.filter(isPriorityVehicleFeature)
  const standardFeatures = sortedFeatures.filter((feature) => !isPriorityVehicleFeature(feature))

  const featuresBlock =
    sortedFeatures.length > 0 ? (
      <Animated.View collapsable={false} entering={enter(4)} style={{ marginTop: SPACING.lg }}>
        <Text
          style={{
            fontSize: 16,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
            marginBottom: SPACING.sm,
          }}
        >
          {t('vehicle_detail.features')}
        </Text>
        {priorityFeatures.length > 0 ? (
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: SPACING.sm,
              marginBottom: standardFeatures.length > 0 ? SPACING.sm : 0,
            }}
          >
            {priorityFeatures.map((feature) => {
              const isDiesel = feature === 'diesel_included'
              const Icon = isDiesel ? Fuel : UserCheck
              const accent = isDiesel ? colors.info : colors.success

              return (
                <View
                  key={feature}
                  style={{
                    flexGrow: 1,
                    flexBasis: 152,
                    minHeight: 72,
                    paddingHorizontal: SPACING.md,
                    paddingVertical: SPACING.sm,
                    backgroundColor: withAlpha(accent, isDark ? 0.15 : 0.08),
                    borderColor: withAlpha(accent, isDark ? 0.34 : 0.2),
                    borderWidth: 1,
                    borderRadius: RADIUS.lg,
                    justifyContent: 'space-between',
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
                    <View
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 10,
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: withAlpha(accent, isDark ? 0.22 : 0.14),
                      }}
                    >
                      <Icon size={17} color={accent} strokeWidth={2} />
                    </View>
                    <Text
                      style={{
                        flex: 1,
                        fontSize: 14,
                        lineHeight: 19,
                        fontFamily: FONTS.bodyBold,
                        color: colors.textPrimary,
                      }}
                    >
                      {t(`listing.vehicle_features.${feature}`, feature)}
                    </Text>
                  </View>
                  <Text
                    style={{
                      marginTop: SPACING.xs,
                      fontSize: 12,
                      fontFamily: FONTS.body,
                      color: accent,
                    }}
                  >
                    {t('vehicle_detail.included_in_rate')}
                  </Text>
                </View>
              )
            })}
          </View>
        ) : null}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.xs }}>
          {standardFeatures.map((feat) => (
            <View
              key={feat}
              style={{
                paddingHorizontal: SPACING.sm,
                paddingVertical: SPACING.xs,
                backgroundColor: colors.surfaceSubtle,
                borderRadius: RADIUS.full,
              }}
            >
              <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.textSecondary }}>
                {t(`listing.vehicle_features.${feat}`, feat)}
              </Text>
            </View>
          ))}
        </View>
      </Animated.View>
    ) : null

  const getInspectionPhotoLabel = (reqId?: string | null) => {
    switch (reqId) {
      case 'hood_open':
        return t('vehicle_detail.inspection_slot_hood_open')
      case 'engine_closeup':
        return t('vehicle_detail.inspection_slot_engine_closeup')
      case 'rim_complete':
        return t('vehicle_detail.inspection_slot_rim_complete')
      case 'rim_serial':
        return t('vehicle_detail.inspection_slot_rim_serial')
      case 'odometer':
        return t('vehicle_detail.inspection_slot_odometer')
      case 'spare_tire':
        return t('vehicle_detail.inspection_slot_spare_tire')
      case 'tools':
        return t('vehicle_detail.inspection_slot_tools')
      default:
        return t('vehicle_detail.inspection_slot_default')
    }
  }

  const inspectionBlock =
    vehicleHasQualitasAsrVerification(vehicle) && inspectionImages.length > 0 ? (
      <Animated.View
        collapsable={false}
        entering={enter(5)}
        style={{
          marginTop: SPACING.lg,
          backgroundColor: colors.surface,
          borderRadius: RADIUS.lg,
          padding: SPACING.md,
          borderWidth: 1,
          borderColor: colors.border,
          ...WEB_SHADOWS.sm,
        }}
      >
        {/*
          Esta tarjeta nombra a Qualitas. Solo se monta cuando el helper
          guest-facing lo permite: cobertura en live, review accepted, y
          sin póliza propia. `flexWrap` evita que el título y el badge se
          corten en anchos estrechos.
        */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: SPACING.xs,
            marginBottom: SPACING.xs,
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.xs,
              flexShrink: 1,
            }}
          >
            <BadgeCheck size={18} color={COLORS.condition.excellent} />
            <Text
              style={{
                fontSize: 16,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
                flexShrink: 1,
              }}
            >
              {t('vehicle_detail.inspection_title')}
            </Text>
          </View>
          <View
            style={{
              paddingHorizontal: SPACING.sm,
              paddingVertical: 2,
              borderRadius: RADIUS.full,
              backgroundColor: colors.surfaceSubtle,
              flexShrink: 0,
            }}
          >
            <Text
              style={{
                fontSize: 12,
                fontFamily: FONTS.bodyBold,
                color: COLORS.condition.excellent,
              }}
            >
              {t('vehicle_detail.inspection_badge')}
            </Text>
          </View>
        </View>

        <Text
          style={{
            fontSize: 13,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
            marginBottom: SPACING.md,
            lineHeight: 18,
          }}
        >
          {t('vehicle_detail.inspection_subtitle')}
        </Text>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ gap: SPACING.sm }}
        >
          {inspectionImages.map((img) => (
            <Pressable
              key={img.id}
              onPress={() => openInspectionLightbox(img.id)}
              accessibilityRole="button"
              accessibilityLabel={t('vehicle_detail.inspection_view_photo')}
              testID={`vehicle-detail.inspection-photo-${img.id}`}
              style={{ width: 120 }}
            >
              <View
                style={{
                  width: 120,
                  height: 80,
                  borderRadius: RADIUS.md,
                  overflow: 'hidden',
                  backgroundColor: colors.surfaceSubtle,
                  borderWidth: img.id === heroImage?.id ? 2 : 1,
                  borderColor: img.id === heroImage?.id ? COLORS.coral.DEFAULT : colors.border,
                }}
              >
                <ExpoImage
                  source={img.url}
                  placeholder={toPlaceholder(img.blurhash)}
                  style={{ width: '100%', height: '100%' }}
                  contentFit="cover"
                />
              </View>
              <Text
                numberOfLines={1}
                style={{
                  fontSize: 12,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                  marginTop: SPACING.xs,
                }}
              >
                {getInspectionPhotoLabel(img.requirementId)}
              </Text>
            </Pressable>
          ))}
        </ScrollView>
      </Animated.View>
    ) : null

  const hostBlock = !isOwner ? (
    <Animated.View
      collapsable={false}
      entering={enter(6)}
      style={{
        marginTop: SPACING.lg,
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        padding: SPACING.md,
        ...WEB_SHADOWS.sm,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginBottom: SPACING.md,
          gap: SPACING.sm,
        }}
      >
        <Text
          style={{
            flex: 1,
            fontSize: 16,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
          }}
        >
          {t('vehicle_detail.host')}
        </Text>
        {vehicle.host?.id ? (
          <BlockUserButton
            targetId={vehicle.host.id}
            targetName={vehicle.host.displayName ?? ''}
            onBlocked={() => router.back()}
          >
            {(open) => (
              <Pressable
                onPress={open}
                hitSlop={10}
                accessibilityRole="button"
                accessibilityLabel={t('block.action')}
                testID="vehicle-detail.block-host"
                style={({ pressed }) => ({
                  // 28x28 with a hitSlop that web ignores.
                  minWidth: INTERACTION.minTouchTarget,
                  minHeight: INTERACTION.minTouchTarget,
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: SPACING.xs,
                  opacity: pressed ? 0.6 : 1,
                })}
              >
                <MoreVertical size={20} color={colors.textSecondary} />
              </Pressable>
            )}
          </BlockUserButton>
        ) : null}
      </View>
      {/*
        LA TARJETA DEL ANFITRIÓN NO ES UN BOTÓN DE CHAT DISFRAZADO.

        Toda esta fila abría una conversación. Tocabas el nombre del anfitrión
        —el gesto natural de "quiero ver quién es"— y la base te contestaba "No
        tenés permisos para esta acción", dos veces si tocabas dos veces. Un
        error como respuesta a la curiosidad.

        Cuando el chat todavía no está abierto, esto vuelve a ser lo que
        parece: una ficha. Sin chevron, sin press, y con una línea que dice
        cuándo se abre. Lo que la persona quiere hacer acá ya está a un dedo de
        distancia: el botón "Reservar ahora" que vive abajo.
      */}
      <Pressable
        onPress={messagingLocked ? undefined : handleContactHost}
        disabled={messagingLocked}
        accessibilityRole={messagingLocked ? 'summary' : 'button'}
        accessibilityLabel={
          messagingLocked
            ? `${vehicle.host.displayName ?? ''}. ${t('messages.locked_title')}`
            : (vehicle.host.displayName ?? '')
        }
        testID="vehicle-detail.host-card"
        style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}
      >
        <Avatar
          size="lg"
          source={vehicle.host.avatarUrl}
          fallback={vehicle.host.displayName ?? '?'}
          accessibilityLabel={vehicle.host.displayName ?? ''}
        />
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 16, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
            {vehicle.host.displayName}
          </Text>
          {vehicle.host.hostRatingAvg != null && (
            <Rating
              value={vehicle.host.hostRatingAvg}
              count={vehicle.host.hostRatingCount}
              size={13}
              showLabel
            />
          )}
          {messagingLocked && (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: SPACING.xs,
                marginTop: 3,
              }}
            >
              <Lock size={12} color={colors.textMuted} strokeWidth={2} />
              <Text
                style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textMuted }}
                numberOfLines={1}
              >
                {t('messages.locked_title')}
              </Text>
            </View>
          )}
        </View>
        {/* Un chevron promete que hay algo del otro lado. Sin chat, no lo hay. */}
        {!messagingLocked && <ChevronRight size={18} color={colors.textMuted} strokeWidth={2} />}
      </Pressable>
    </Animated.View>
  ) : null

  const reviewsBlock =
    vehicle.reviews.length > 0 ? (
      <Animated.View collapsable={false} entering={enter(7)} style={{ marginTop: SPACING.lg }}>
        <Text
          style={{
            fontSize: 16,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
            marginBottom: SPACING.sm,
          }}
        >
          {t('vehicle_detail.reviews')} ({vehicle.reviewCount})
        </Text>
        {vehicle.reviews.slice(0, 3).map((review) => {
          const reviewerName = review.reviewerDisplayName?.trim() || t('reviews.reviewer_fallback')
          const comment = review.comment?.trim() ?? ''

          return (
            <View
              key={review.id}
              testID="vehicle-detail.review-card"
              style={{
                marginBottom: SPACING.md,
                backgroundColor: colors.surface,
                borderRadius: RADIUS.lg,
                padding: SPACING.md,
                ...WEB_SHADOWS.sm,
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: SPACING.sm,
                  marginBottom: SPACING.sm,
                }}
              >
                <Avatar
                  size="sm"
                  source={review.reviewerAvatarUrl}
                  fallback={reviewerName}
                  accessibilityLabel={reviewerName}
                />
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textPrimary }}>
                    {reviewerName}
                  </Text>
                  <Rating value={review.rating} size={12} />
                </View>
                <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textMuted }}>
                  {formatBookingMonthYear(review.createdAt, i18n.language, 'short')}
                </Text>
              </View>
              <Text
                style={{
                  fontSize: 14,
                  fontFamily: FONTS.body,
                  color: comment ? colors.textSecondary : colors.textMuted,
                  lineHeight: 20,
                }}
              >
                {comment || t('reviews.rating_only')}
              </Text>
            </View>
          )
        })}
      </Animated.View>
    ) : null

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP: Airbnb/Turo-style — gallery grid + two-column (content + sticky sidebar)
  // ══════════════════════════════════════════════════════════════════════════
  if (isDesktop) {
    return (
      <>
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: SPACING['3xl'] }}
        >
          {/* Back button */}
          <View style={{ paddingHorizontal: SPACING.lg, paddingVertical: SPACING.sm }}>
            <BackButton fallbackHref="/(tabs)/explore" preferFallback />
          </View>

          {/* Image gallery grid */}
          <View style={{ paddingHorizontal: SPACING.lg }}>
            <View
              testID="vehicle-detail.image-carousel"
              style={{
                flexDirection: 'row',
                height: HERO_HEIGHT_DESKTOP,
                borderRadius: RADIUS.lg,
                overflow: 'hidden',
                gap: SPACING.xs,
              }}
            >
              {/* Large hero (left 60%) */}
              <Pressable
                onPress={() => heroImage && openGalleryLightbox(heroImage.id)}
                accessibilityRole="button"
                accessibilityLabel={t('vehicle_detail.view_image')}
                testID="vehicle-detail.hero-photo"
                style={{ flex: 3 }}
              >
                <View style={{ flex: 1, backgroundColor: colors.surfaceSubtle }}>
                  {heroImage ? (
                    <ExpoImage
                      source={heroImage.url}
                      placeholder={toPlaceholder(heroImage.blurhash)}
                      style={{ width: '100%', height: '100%' }}
                      contentFit="cover"
                    />
                  ) : null}
                </View>
              </Pressable>
              {/* Right thumbnails (40%, 2 rows) */}
              {galleryImages.length > 1 && (
                <View style={{ flex: 2, gap: SPACING.xs }}>
                  {galleryImages.slice(1, 3).map((img, thumbIndex) => (
                    <Pressable
                      key={img.id}
                      onPress={() => openGalleryLightbox(img.id)}
                      accessibilityRole="button"
                      accessibilityLabel={
                        thumbIndex === 1 && galleryImages.length > 3
                          ? t('photo_lightbox.see_all', { count: galleryImages.length })
                          : t('vehicle_detail.view_image')
                      }
                      testID={`vehicle-detail.gallery-photo-${thumbIndex + 1}`}
                      style={{ flex: 1 }}
                    >
                      <View style={{ flex: 1, backgroundColor: colors.surfaceSubtle }}>
                        <ExpoImage
                          source={img.url}
                          placeholder={toPlaceholder(img.blurhash)}
                          style={{ width: '100%', height: '100%' }}
                          contentFit="cover"
                        />
                        {thumbIndex === 1 && galleryImages.length > 3 ? (
                          <View
                            style={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              bottom: 0,
                              left: 0,
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: withAlpha(COLORS.black, 0.45),
                            }}
                          >
                            <Text
                              style={{
                                fontFamily: FONTS.body,
                                fontSize: 14,
                                color: COLORS.white,
                                textAlign: 'center',
                                paddingHorizontal: SPACING.sm,
                              }}
                            >
                              {t('photo_lightbox.see_all', { count: galleryImages.length })}
                            </Text>
                          </View>
                        ) : null}
                      </View>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>
          </View>

          {/* Two-column: content + sticky sidebar */}
          <View
            style={{
              flexDirection: 'row',
              paddingHorizontal: SPACING.lg,
              marginTop: SPACING.lg,
              gap: SPACING.xl,
            }}
          >
            {/* Left: content */}
            <View style={{ flex: 3 }}>
              {titleBlock}
              {readyBlock}
              {descriptionBlock}
              {specsBlock}
              {featuresBlock}
              {inspectionBlock}
              {hostBlock}
              {reviewsBlock}
            </View>

            {/* Right: sticky booking card */}
            <View style={{ flex: 2, maxWidth: 360 }}>
              <View
                style={{
                  position: 'sticky' as 'relative',
                  top: SPACING.lg,
                  backgroundColor: colors.surface,
                  borderRadius: RADIUS.lg,
                  padding: SPACING.lg,
                  ...WEB_SHADOWS.md,
                }}
              >
                <View
                  style={{ flexDirection: 'row', alignItems: 'baseline', marginBottom: SPACING.sm }}
                >
                  <Text
                    testID="vehicle-detail.price"
                    style={{ fontSize: 28, fontFamily: FONTS.bodyBold, color: COLORS.amber.dark }}
                  >
                    {ratePrice}
                  </Text>
                  <Text
                    style={{
                      fontSize: 15,
                      fontFamily: FONTS.body,
                      color: colors.textSecondary,
                      marginLeft: SPACING.xs,
                    }}
                  >
                    {rateLabel}
                  </Text>
                </View>
                {vehicle.isInstantBook && isBookable && (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: SPACING.xs,
                      marginBottom: SPACING.md,
                    }}
                  >
                    <Zap size={14} color={COLORS.amber.dark} fill={COLORS.amber.dark} />
                    <Text
                      style={{ fontSize: 13, fontFamily: FONTS.bodyBold, color: COLORS.amber.dark }}
                    >
                      {t('vehicle_detail.instant_book_badge')}
                    </Text>
                  </View>
                )}
                <View
                  style={{ height: 1, backgroundColor: colors.border, marginBottom: SPACING.md }}
                />
                {bookButton('lg')}
                {!isOwner && !isBookable && (
                  <Text
                    style={{
                      marginTop: SPACING.sm,
                      fontSize: 12,
                      lineHeight: 17,
                      fontFamily: FONTS.body,
                      color: colors.textSecondary,
                      textAlign: 'center',
                    }}
                  >
                    {t('vehicle_detail.natural_vehicle_booking_block')}
                  </Text>
                )}
                {!isOwner && !messagingLocked && (
                  <Pressable
                    onPress={handleContactHost}
                    accessibilityRole="button"
                    accessibilityLabel={t('vehicle_detail.contact_host')}
                    testID="vehicle-detail.contact-host-sidebar"
                    style={{
                      marginTop: SPACING.sm,
                      alignItems: 'center',
                      paddingVertical: SPACING.sm,
                    }}
                  >
                    <Text style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textBrand }}>
                      {t('vehicle_detail.contact_host')}
                    </Text>
                  </Pressable>
                )}
              </View>
            </View>
          </View>
        </ScrollView>
        {lightboxEl}
      </>
    )
  }

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE: Vertical scroll above a docked CTA bar (siblings, never stacked)
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <>
      <ScrollView
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ paddingBottom: SPACING.lg }}
      >
        {/* Hero image */}
        <View
          testID="vehicle-detail.image-carousel"
          style={{ height: HERO_HEIGHT_MOBILE, backgroundColor: colors.surfaceSubtle }}
        >
          {heroImage ? (
            <Pressable
              onPress={() => openGalleryLightbox(heroImage.id)}
              accessibilityRole="button"
              accessibilityLabel={t('vehicle_detail.view_image')}
              testID="vehicle-detail.hero-photo"
              style={{ flex: 1 }}
            >
              <ExpoImage
                source={heroImage.url}
                placeholder={toPlaceholder(heroImage.blurhash)}
                style={{ width: '100%', height: '100%' }}
                contentFit="cover"
              />
            </Pressable>
          ) : null}
          <View style={{ position: 'absolute', top: insets.top + SPACING.sm, left: SPACING.lg }}>
            <BackButton fallbackHref="/(tabs)/explore" preferFallback />
          </View>
        </View>

        {/* Image thumbnails */}
        {galleryImages.length > 1 && (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{
              paddingHorizontal: SPACING.lg,
              paddingTop: SPACING.sm,
              gap: SPACING.xs,
            }}
          >
            {galleryImages.slice(0, 8).map((img) => (
              <Pressable
                key={img.id}
                onPress={() => openGalleryLightbox(img.id)}
                accessibilityRole="button"
                accessibilityLabel={t('vehicle_detail.view_image')}
                testID={`vehicle-detail.gallery-thumb-${img.id}`}
              >
                <View
                  style={{
                    width: 60,
                    height: 48,
                    borderRadius: RADIUS.sm,
                    overflow: 'hidden',
                    borderWidth: img.id === heroImage?.id ? 2 : 0,
                    borderColor: COLORS.coral.DEFAULT,
                    opacity: img.id === heroImage?.id ? 1 : 0.7,
                  }}
                >
                  <ExpoImage
                    source={img.url}
                    placeholder={toPlaceholder(img.blurhash)}
                    style={{ width: '100%', height: '100%' }}
                    contentFit="cover"
                  />
                </View>
              </Pressable>
            ))}
          </ScrollView>
        )}

        <View style={{ paddingHorizontal: SPACING.lg }}>
          {titleBlock}
          {readyBlock}
          {descriptionBlock}
          {specsBlock}
          {featuresBlock}
          {inspectionBlock}
          {hostBlock}
          {reviewsBlock}
        </View>
      </ScrollView>

      {/* Bottom CTA (mobile only).
          The bar used to repeat the headline price to the left of the button,
          which pushed "Reservar ahora" to x=178 — off the gutter every other
          docked CTA in the app starts from. The price already lives under the
          title; the bar is now just the action, full width.

          It is a flex SIBLING of the ScrollView, never an overlay — the same
          rule the tab bar follows in `(tabs)/_layout.tsx`. As an absolute
          overlay it painted and hit-tested on top of the scroll viewport, so
          anything the user scrolled to the bottom of the list came to rest
          UNDERNEATH it: still in the hierarchy, still reported visible, but
          every tap landing on the button instead. `vehicle-detail.block-host`
          did exactly that at 320x640 (a 7px sliver, reported 100% visible),
          which is why the block-modal E2E kept opening the booking screen.
          The old `contentContainerStyle.paddingBottom` was a hand-maintained
          guess at this bar's height that only cleared it at the very END of
          the scroll. As a sibling the viewport ends where the bar begins, so
          hierarchy-visibility and tappability agree at every screen size and
          the magic number is gone. */}
      <View
        testID="vehicle-detail.book-bar"
        collapsable={false}
        style={{
          paddingHorizontal: SPACING.lg,
          paddingTop: SPACING.sm,
          paddingBottom: Math.max(insets.bottom, SPACING.sm),
          backgroundColor: colors.background,
          borderTopWidth: 1,
          borderTopColor: colors.border,
          zIndex: Z_INDEX.raised,
        }}
      >
        {bookButton('md')}
      </View>
      {lightboxEl}
    </>
  )
}
